import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useRef, useState } from "react";
import { useSlateStatic } from "slate-react";
import { Transforms } from "slate";
import IntlMessageFormat from "intl-messageformat";
import { DeleteIcon } from "../../../../icons/DeleteIcon";
import { EditIcon } from "../../../../icons/EditIcon";
import { ExternalLinkIcon } from "../../../../icons/ExternalLinkIcon";
import { VerticalAlignIcon } from "../../../../icons/VerticalAlignIcon";
import { LeftJustifiedIcon } from "../../../../icons/LeftJustifiedIcon";
import { FocusList } from "../../../../utilities/focus-list/FocusList";
import { MenuList } from "../../../../menu-list/MenuList";
import { PopperBox } from "../../../../popper/PopperBox";
import { Toolbar } from "../../../../toolbar/Toolbar";
import { ToolbarLinkButton } from "../../../../toolbar/ToolbarLinkButton";
import { ToolbarGroup } from "../../../../toolbar/ToolbarGroup";
import { ToolbarButton } from "../../../../toolbar/ToolbarButton";
import { useSlateRTE } from "../../SlateRTEContext";
import { getParentLink, removeLink, updateLink, wrapInLink } from "../links/link-utilities";
import { focusEditor } from "../../utilities/index";
import { ImageDialog } from "./ImageDialog";
import { deleteImage, updateImage } from "./Image.utils";
const verticalAlignOptions = ["top", "bottom", "middle", "baseline"];
const horizontalAlignOptions = ["left", "right", "center", "inline"];
export const ImageToolbar = forwardRef((props, ref) => {
    const { imageProperties, path, element, horizontalAlign = "inline", verticalAlign = "baseline", onVerticalAlign, onHorizontalAlign, ...toolbarProps } = props;
    const editor = useSlateStatic();
    const { stringMap } = useSlateRTE();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const previousStateRef = useRef(imageProperties);
    const openDialog = () => {
        previousStateRef.current = imageProperties;
        setIsDialogOpen(true);
    };
    const [parentLink] = getParentLink(editor, path);
    const closeDialog = () => setIsDialogOpen(false);
    return (_jsxs(Toolbar, { shouldAutoFocus: true, ref: ref, ...toolbarProps, children: [_jsxs(ToolbarGroup, { children: [_jsx(ToolbarButton, { label: stringMap.delete, icon: _jsx(DeleteIcon, { "aria-hidden": "true" }), onClick: () => deleteImage(editor, element) }), _jsx(ToolbarButton, { label: stringMap.editImage, icon: _jsx(EditIcon, { "aria-hidden": "true" }), onClick: openDialog }), _jsx(ToolbarButton, { label: stringMap.alignHorizontal, icon: _jsx(LeftJustifiedIcon, { "aria-hidden": "true" }), menu: () => (_jsx(PopperBox, { hasPadding: false, children: _jsx(FocusList, { children: _jsx(MenuList, { mode: "select", items: horizontalAlignOptions.map((align) => ({
                                        label: stringMap[align],
                                        isSelected: horizontalAlign === align,
                                        onClick: () => onHorizontalAlign(align)
                                    })) }) }) })) }), horizontalAlign === "inline" && (_jsx(ToolbarButton, { label: stringMap.alignVertical, icon: _jsx(VerticalAlignIcon, { "aria-hidden": "true" }), menu: () => (_jsx(PopperBox, { hasPadding: false, children: _jsx(FocusList, { children: _jsx(MenuList, { mode: "select", items: verticalAlignOptions.map((align) => ({
                                        label: stringMap[align],
                                        isSelected: verticalAlign === align,
                                        onClick: () => onVerticalAlign(align)
                                    })) }) }) })) })), parentLink ? (_jsx(ToolbarLinkButton, { icon: _jsx(ExternalLinkIcon, { "aria-label": stringMap.visitLink }), href: parentLink.href, target: parentLink.openInNewTab ? "_blank" : "", label: _jsx("span", { style: {
                                display: "block",
                                overflow: "hidden",
                                maxWidth: 200,
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis"
                            }, children: typeof stringMap.visitLinkHref === "function"
                                ? stringMap.visitLinkHref(parentLink.href)
                                : new IntlMessageFormat(stringMap.visitLinkHref).format({ href: parentLink.href }) }) })) : null] }), _jsx(ImageDialog, { isOpen: isDialogOpen, onClose: () => {
                    closeDialog();
                    focusEditor(editor);
                }, linkProperties: parentLink, imageProperties: imageProperties, onApply: (imageState, { href, openInNewTab }) => {
                    closeDialog();
                    Transforms.select(editor, path);
                    updateImage(editor, element, imageState);
                    if (href) {
                        if (parentLink) {
                            updateLink({ editor, element: parentLink, href, openInNewTab });
                        }
                        else {
                            // wrapInLink
                            wrapInLink({ editor, path, openInNewTab, href });
                        }
                    }
                    else if (parentLink) {
                        // unwrap link
                        removeLink(editor, { element: parentLink });
                    }
                    focusEditor(editor);
                }, onCancel: () => {
                    closeDialog();
                }, onDelete: () => {
                    deleteImage(editor, element);
                    closeDialog();
                    focusEditor(editor);
                } })] }));
});
